/**
 * @file Admin tenant (client) listing page.
 *
 * Presents a paginated, server-filtered table of top-level tenants (`Client` records
 * from ai-auth) with nested company counts. List state is driven by URL search params
 * so filters, sort, and page size are bookmarkable and survive refresh.
 *
 * @see {@link useClientsWithCompaniesQuery} — `GET /auth/api/v1/auth/clients`
 *
 * URL search params:
 * - `page`, `pageSize` — pagination
 * - `search` — case-insensitive name filter (committed after debounce or Enter)
 * - `sortBy`, `sortOrder` — server sort (`createdAt` | `updatedAt`, `asc` | `desc`);
 *   default `updatedAt` / `desc` via {@link DEFAULT_CLIENTS_LIST_ROUTE_SEARCH}; omitted after cancel
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import PageTransition from '@/components/ui/PageTransition';
import { useClientsWithCompaniesQuery } from '@/api/auth/queries';
import type { ClientListSortBy, ClientWithCompanies, SortOrder } from '@/api/auth/interfaces';
import { Card, Table, Button, Typography, Input, Empty, theme } from 'antd';
import { PartitionOutlined, PlusOutlined, ClearOutlined } from '@ant-design/icons';
import type { SorterResult, TableCurrentDataSource, TablePaginationConfig } from 'antd/es/table/interface';
import { AddEditClientModal } from '@/components/admin/AddEditClientModal';
import { useTenantListColumns } from '@/components/tenant/useTenantListColumns';
import { PageHeader } from '@/components/ui/PageHeader';
import { FilterBar } from '@/components/ui/FilterBar';
import { useUrlSearchState } from '@/hooks/useUrlSearchState';

const { Text } = Typography;

type TenantsSearch = {
  page: number;
  pageSize: number;
  search?: string;
  sortBy?: ClientListSortBy;
  sortOrder?: SortOrder;
};

/** Builds route search, including sort only when both fields are set. */
const buildTenantsSearch = ({
  page,
  pageSize,
  search,
  sortBy,
  sortOrder
}: TenantsSearch): TenantsSearch => ({
  page,
  pageSize,
  ...(search ? { search } : {}),
  ...(sortBy && sortOrder ? { sortBy, sortOrder } : {})
});

/** Route definition: validates and normalizes `/admin/tenants/` search params. */
export const Route = createFileRoute('/admin/tenants/')({
  validateSearch: (search) =>
    z
      .object({
        page: z.coerce.number().int().min(1).catch(1),
        pageSize: z.coerce.number().int().min(1).max(100).catch(10),
        search: z.string().optional().catch(undefined),
        sortBy: z.enum(['createdAt', 'updatedAt']).optional().catch(undefined),
        sortOrder: z.enum(['asc', 'desc']).optional().catch(undefined)
      })
      .parse(search),
  component: TenantsPage
});

/**
 * Admin tenants list: search, sort, paginate, and open create/manage flows.
 *
 * **URL vs input:** `search` in the address bar is the committed filter. The search
 * field keeps a local `searchDraft` so typing feels instant; the draft is debounced
 * into the URL. **Sort** is server-side via optional query params (cancel clears them).
 */
function TenantsPage() {
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientWithCompanies | null>(null);

  const navigate = Route.useNavigate();
  const { page, pageSize, search, sortBy, sortOrder } = Route.useSearch();
  const hasSort = sortBy != null && sortOrder != null;

  /** Name filter currently applied (from URL); drives the API query. */
  const committedSearch = search ?? '';

  /** Writes the name filter to the URL and resets to page 1. */
  const applySearchToUrl = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      navigate({
        to: '/admin/tenants/',
        replace: true,
        search: buildTenantsSearch({
          page: 1,
          pageSize,
          search: trimmed || undefined,
          sortBy,
          sortOrder
        })
      } as any);
    },
    [navigate, pageSize, sortBy, sortOrder]
  );

  const { searchDraft, setSearchDraft, changeDraft, searchNow, isSearchPending, isFiltering } =
    useUrlSearchState(committedSearch, applySearchToUrl);

  const { data: clientsData, isLoading, isFetching } = useClientsWithCompaniesQuery({
    page,
    limit: pageSize,
    search: committedSearch || undefined,
    ...(hasSort ? { sortBy, sortOrder } : {})
  });
  const clients: ClientWithCompanies[] = useMemo(() => clientsData?.clients ?? [], [clientsData]);
  const total = clientsData?.pagination?.total ?? clients.length;

  /** Clears the name filter; keeps pagination size and current sort (if any). */
  const handleClearFilters = useCallback(() => {
    setSearchDraft('');
    navigate({
      to: '/admin/tenants/',
      replace: true,
      search: buildTenantsSearch({ page: 1, pageSize, sortBy, sortOrder })
    } as any);
  }, [navigate, pageSize, sortBy, sortOrder, setSearchDraft]);

  const openAddClient = useCallback(() => {
    setEditingClient(null);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingClient(null);
  }, []);

  /** Handles pagination and column sort via Table `onChange` (`extra.action`). */
  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: unknown,
      sorter: SorterResult<ClientWithCompanies> | SorterResult<ClientWithCompanies>[],
      extra: TableCurrentDataSource<ClientWithCompanies>
    ) => {
      if (extra.action === 'paginate') {
        navigate({
          to: '/admin/tenants/',
          search: buildTenantsSearch({
            page: pagination.current ?? 1,
            pageSize: pagination.pageSize ?? pageSize,
            search: committedSearch || undefined,
            sortBy,
            sortOrder
          })
        } as any);
        return;
      }

      if (extra.action !== 'sort') return;

      const col = Array.isArray(sorter) ? sorter[0] : sorter;
      const order = col?.order;

      if (!order) {
        navigate({
          to: '/admin/tenants/',
          search: {
            ...buildTenantsSearch({
              page: 1,
              pageSize,
              search: committedSearch || undefined
            }),
            sortBy: undefined,
            sortOrder: undefined
          }
        } as any);
        return;
      }

      const columnKey = col.columnKey ?? col.field;
      const nextSortBy: ClientListSortBy | undefined =
        columnKey === 'updatedAt' ? 'updatedAt' : columnKey === 'createdAt' ? 'createdAt' : undefined;
      if (!nextSortBy) return;

      const nextSortOrder: SortOrder = order === 'ascend' ? 'asc' : 'desc';

      navigate({
        to: '/admin/tenants/',
        search: buildTenantsSearch({
          page: 1,
          pageSize,
          search: committedSearch || undefined,
          sortBy: nextSortBy,
          sortOrder: nextSortOrder
        })
      } as any);
    },
    [navigate, pageSize, committedSearch, sortBy, sortOrder]
  );

  const columns = useTenantListColumns(sortBy, sortOrder);

  /** Show loading on first fetch, or while refetching during an active search change. */
  const tableLoading = isLoading || (isFetching && (isSearchPending || isFiltering));

  return (
    <PageTransition animation="fadeInUp">
      <PageHeader
        icon={<PartitionOutlined />}
        title={t('tenants.title')}
        description={t('tenants.subtitle')}
        actions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddClient}>
            {t('tenants.addClient')}
          </Button>
        }
      />

      <FilterBar
        actions={
          isFiltering && !isSearchPending ? (
            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
              {t('tenants.clearFilters')}
            </Button>
          ) : undefined
        }
      >
        <Input.Search
          allowClear
          placeholder={t('tenants.searchPlaceholder')}
          aria-label={t('tenants.search')}
          value={searchDraft}
          loading={isFetching && isSearchPending}
          style={{ flex: '1 1 220px', minWidth: 160, maxWidth: 420 }}
          onChange={(e) => changeDraft(e.target.value)}
          onSearch={searchNow}
        />
        {(isFiltering || isSearchPending) && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {isSearchPending
              ? t('tenants.searchPending')
              : t('tenants.searchResults', { count: total, query: committedSearch })}
          </Text>
        )}
      </FilterBar>

      <Card
        styles={{ body: { padding: 0 } }}
      >
        <Table
          columns={columns}
          dataSource={clients}
          rowKey="id"
          loading={tableLoading}
          onChange={handleTableChange}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={isFiltering ? t('tenants.noSearchResults', { query: committedSearch }) : t('tenants.empty')}
              />
            )
          }}
          pagination={{
            style: { paddingInline: token.padding },
            current: page,
            pageSize,
            showQuickJumper: true,
            total,
            showTotal: (total, range) => t('tenants.paginationTotal', { start: range[0], end: range[1], total }),
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <AddEditClientModal
        open={modalOpen}
        onClose={closeModal}
        editingClient={editingClient}
      />
    </PageTransition>
  );
}
